import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, Container, Typography } from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import AddIcon from '@mui/icons-material/Add';

import { di } from '../../state/inversify.config';
import { GlobalQueryParams } from '../../state/queryParams/queryParams.interface';
import { useQuerySync } from '../../state/queryParams/useQuerySync';
import { UserRepository } from '../../state/reduxRepositories';
import { UserQueryParamService } from './user.queryParam.service';
import { UserPageQueryParams } from './user.queryParams.interface';
import { UserCard } from './components/userCard.dumb';
import { UserForm } from './user.form';
import { LoginDomainService } from '../login/loginDomainService';
import { AuthService } from '../../state/helperServices/auth/auth.service';
import { Banner } from '../../components/banner/banner';
import { AlertDialog } from '../../components/alertDialog/alertDialog';
import { Button } from '../../components/button/button';

export function UserPage() {
  const { t } = useTranslation();

  const userQueryParamService = di.get<UserQueryParamService>(
    UserQueryParamService,
  );
  useQuerySync<UserPageQueryParams & GlobalQueryParams>(userQueryParamService);
  const queryParams = userQueryParamService.useSelectMergedQuery();
  const selectedUserId = queryParams?.selectedUserId;

  const userRepository = di.get<UserRepository>(UserRepository);
  const users = userRepository.useResourcesSelector();

  // TODO: move (all logic) into a domainService and only return accountType or even better find a way so that you don't have to send down the loggedInUser
  const authService = di.get<AuthService>(AuthService);
  const loggedInUser = authService.useLoggedInUserSelector();

  const loginDomainService = di.get<LoginDomainService>(LoginDomainService);
  const isNetworkConnected = loginDomainService.useNetworkSelector();

  const [action, setAction] = useState<'edit' | 'create' | boolean>(false);
  const [modalHeading, setModalHeading] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const onEdit = (id: string) => {
    userQueryParamService.dispatchQueryParams({
      selectedUserId: id,
    });
    setAction('edit');
    setModalHeading(`${t('EDIT_USER')}`);
  };

  const onCreate = () => {
    setAction('create');
    setModalHeading(`${t('CREATE_USER')}`);
  };

  return (
    <Container sx={{ mb: 2 }}>
      {!isNetworkConnected && (
        <Banner
          title={t('OFFLINE_MODE')}
          severity="error"
          icon={<WarningIcon />}
        />
      )}
      <Typography
        id="user-title"
        variant="h5"
        component="h5"
        fontWeight="bold"
        sx={{ my: 2 }}
      >
        {t('ACCOUNT_USERS')}
      </Typography>
      {isNetworkConnected && (
        <Button
          color="secondary"
          testId="createUser"
          size="large"
          type="submit"
          variant="contained"
          onClick={onCreate}
          label={`${t('CREATE_USER')}`}
          startIcon={<AddIcon />}
          sx={{ my: 1 }}
        />
      )}
      <Grid container columnGap={2} rowSpacing={2} sx={{ mt: 2 }}>
        {users.map((user) => {
          return (
            <Grid
              item
              xs={12}
              sm={12}
              md={8}
              lg={4}
              key={user.id}
              sx={{ maxWidth: '350px !important' }}
            >
              <UserCard
                loginUser={loggedInUser}
                onEdit={onEdit}
                user={user}
                isNetworkConnected={isNetworkConnected}
              />
            </Grid>
          );
        })}
      </Grid>
      <AlertDialog
        title={modalHeading}
        open={!!action}
        setOpen={() => setAction(false)}
        content={<UserForm setMode={setAction} mode={action} />}
      />
      {/* <AlertDialog
        actions
        title={`${t('DELETE_USER')}`}
        open={showConfirmModal}
        setOpen={setShowConfirmModal}
        content={`${t('USER_DELETE_CONFIRMATION')}`}
        cancelLabel={t('CANCEL')}
        confirmLabel={t('CONFIRM')}
        onDismiss={() => setShowConfirmModal(false)}
        onConfirm={() => {
          if (selectedUserId) userRepository.dispatchDelete(selectedUserId);
          setShowConfirmModal(false);
        }}
      /> */}
    </Container>
  );
}
